import type { HelpArticle } from "../../types";
import {
  AUTOSIZE_PICK,
  MAPLE_PROJECT,
  PICKER_FAIL_2X10,
  PICKER_PASS_2X12,
} from "../../../calculations/calcPreviewFixtures";

const pct = (value: number): string => `${Math.round(value * 100)}%`;

export const memberPickerArticle: HelpArticle = {
  id: "member-picker",
  title: "The size & grade picker",
  description:
    "Browse catalog sections scored against the active member, filter the returned verdicts, and apply one section or use sawn-lumber Autosize.",
  tags: ["wood", "member database", "picker", "autosize", "size", "grade", "DCR"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Select… scores catalog candidates against the active sheet's committed geometry, loads, conditions, and design method. The table compares sections under one demand model; it is not a list of standalone catalog capacities.",
    },

    { kind: "heading", level: 2, text: "Open a live candidate sweep", id: "open" },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, "FJ-1", "A. Key properties", "Select…"] },
    {
      kind: "paragraph",
      text: "Opening the picker prepares canonical inputs from the active member and requests scored candidates. If required member inputs are incomplete or unsupported, the sheet reports the problem instead of presenting scores from a default member. The role supplies the initial size and material-family scope; **Include all sizes** removes the role-size restriction but does not create catalog rows that do not exist.",
    },
    {
      kind: "shot",
      shotId: "wood-picker-table",
      caption: "FJ-1's deterministic candidate sweep sorted by governing DCR: per-check values beside the maximum and rows whose all-checks verdict fails.",
    },

    { kind: "heading", level: 2, text: "Read the verdict and governing value separately", id: "verdict" },
    {
      kind: "paragraph",
      text: "The runtime defines a candidate's **passes** value as `all(check.passes for check in checks)`. It defines **maxDcr** as the maximum non-null DCR returned by those checks. Firmark therefore preserves both fields: **Passing only** filters `passes`, while the Governing column displays and sorts `maxDcr`. The interface does not substitute one predicate for the other.",
    },
    {
      kind: "definition-list",
      items: [
        { term: "Identity", definition: "Family, manufacturer when applicable, species, grade or designation, and nominal size identify the catalog candidate." },
        { term: "Per-check DCR", definition: "Each visible metric is returned for that candidate under the active member inputs." },
        { term: "Governing", definition: "The largest non-null returned check DCR; rows without a sortable value are placed after rows that have one." },
        { term: "Pass", definition: "True only when every returned check passes." },
        { term: "Current", definition: "The section already seated on the sheet." },
      ],
    },
    {
      kind: "values",
      caption: "Three returned rows from the FJ-1 worked example.",
      rows: [
        { label: `${PICKER_FAIL_2X10.size} · ${PICKER_FAIL_2X10.species} ${PICKER_FAIL_2X10.grade}`, value: `${pct(PICKER_FAIL_2X10.maxDcr)} maximum · fails` },
        { label: `${AUTOSIZE_PICK.size} · ${AUTOSIZE_PICK.species} ${AUTOSIZE_PICK.grade}`, value: `${pct(AUTOSIZE_PICK.maxDcr)} maximum · passes` },
        { label: `${PICKER_PASS_2X12.size} · ${PICKER_PASS_2X12.species} ${PICKER_PASS_2X12.grade}`, value: `${pct(PICKER_PASS_2X12.maxDcr)} maximum · passes` },
      ],
    },
    {
      kind: "callout",
      variant: "note",
      title: "Scores belong to the current input snapshot",
      body: "A row is not a reusable capacity statement. Change the member geometry, loading, conditions, or method and the candidate sweep must be run again.",
    },

    { kind: "heading", level: 2, text: "Search and filter the returned rows", id: "filters" },
    {
      kind: "paragraph",
      text: "Search matches designation, manufacturer, species, grade, or size. Family, manufacturer, species, grade, size, and actual width or depth filters combine with that search. The role controls which DCR columns are eligible, and Firmark hides an eligible metric when no returned row carries a value for it.",
    },
    {
      kind: "shot",
      shotId: "wood-picker-search",
      caption: "Text search and catalog facets narrow one scored response without changing the active member.",
    },
    {
      kind: "paragraph",
      text: "The modal distinguishes scoring, request failure, an unavailable unscored database, and a scored set with no rows matching the current filters. Closing with Escape or the close control leaves the member unchanged. Enter or Space applies the focused row.",
    },
    {
      kind: "shot",
      shotId: "wood-picker-passing",
      caption: "Passing only keeps candidates whose returned checks all pass; the footer reports the filtered count against the full sweep.",
    },

    { kind: "heading", level: 2, text: "Apply one section deliberately", id: "apply" },
    {
      kind: "steps",
      steps: [
        { title: "Commit the demand model", body: "Leave any pending geometry, load, or condition field before opening the picker." },
        { title: "Narrow the catalog", body: "Use search and facets to compare the intended material and size set." },
        { title: "Read every relevant check", body: "Use the governing maximum for orientation, then review the other returned checks and the project criteria outside this sweep." },
        { title: "Apply the row", body: "Firmark seats the catalog identity and geometry, closes the picker, and recomputes the persisted sheet." },
      ],
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Passing is not a complete specification",
      body: "Availability, detailing, vibration, fire, durability, connections, holes, bearing configuration, and other project criteria may sit outside the candidate score. Selection remains an engineering decision.",
    },

    { kind: "heading", level: 2, text: "What Autosize does", id: "autosize" },
    {
      kind: "paragraph",
      text: `Autosize sweeps only the role's scoped sawn-lumber catalog and discards candidates whose all-checks verdict fails. It orders the remaining rows by smallest cross-sectional area, then shallowest depth, then lowest maximum DCR. In the FJ-1 worked example it seats ${AUTOSIZE_PICK.size} ${AUTOSIZE_PICK.species} ${AUTOSIZE_PICK.grade} at ${pct(AUTOSIZE_PICK.maxDcr)} maximum DCR. Engineered families require a deliberate picker selection.`,
    },
    {
      kind: "paragraph",
      text: "If no scoped sawn candidate passes, Autosize returns no section and leaves the member unchanged. It does not silently convert the sheet to another material family.",
    },

    { kind: "related", topics: ["member-sheets", "design-conditions", "your-first-member", "member-results"] },
  ],
};
